import React, { useState, useEffect, useCallback } from 'react';
import type { PendingDeposit, CountryPolicyV2 } from '../v2/types';
import { RuleMatchDetailOverlay, type RuleMatchInfo } from './RuleMatchDetailOverlay';
import {
  listPendingDeposits,
  rejectPendingDeposit,
  settlePendingDeposit,
  syncFromGeoLink,
  prepareDepositXdr,
  type SettleParams,
} from '../v2/services/pendingDepositService';
import { numericToAlpha2 } from '../v2/config';
import { stroopsToDisplay } from '../v2/services/vaultService';

// Browser wallet (Freighter, Rabet, xBull, etc.) window type
declare global {
  interface Window {
    freighter?: {
      isConnected: () => Promise<boolean>;
      signTransaction: (
        xdr: string,
        opts?: { networkPassphrase?: string },
      ) => Promise<{ signedTransaction: string } | string>;
    };
  }
}

// ── Color tokens — light theme matching GeoLink / country overlays ────────────
const C = {
  bg:           '#fff',
  bgCard:       '#f8f9fa',
  bgCardHover:  '#f0f0f0',
  border:       '#ddd',
  borderFaint:  '#eee',
  text:         '#333',
  textMuted:    '#555',
  textFaint:    '#888',
  gold:         '#FFD700',
  goldDark:     '#996600',
  goldDim:      'rgba(255,215,0,0.15)',
  green:        '#28a745',
  greenDim:     'rgba(40,167,69,0.1)',
  amber:        '#fd7e14',
  amberDim:     'rgba(253,126,20,0.1)',
  blue:         '#007bff',
  blueDim:      'rgba(0,123,255,0.1)',
  red:          '#dc3545',
  redDim:       'rgba(220,53,69,0.1)',
};

const DECISION_CONFIG: Record<PendingDeposit['geoTrustDecision'], {
  label: string; color: string; dim: string; border: string; icon: string;
}> = {
  pending:  { label: 'Pending',  color: C.amber,  dim: C.amberDim,  border: '#fd7e1444', icon: '⏳' },
  approved: { label: 'Approved', color: C.blue,   dim: C.blueDim,   border: '#007bff44', icon: '✓'  },
  rejected: { label: 'Rejected', color: C.red,    dim: C.redDim,    border: '#dc354544', icon: '✕'  },
  settled:  { label: 'Settled',  color: C.green,  dim: C.greenDim,  border: '#28a74544', icon: '⬆'  },
};

interface GeoLinkPendingDepositsPanelProps {
  walletPublicKey: string | null;
  /** C-address of the linked passkey smart account (used for balance check) */
  passkeyAccountAddress?: string | null;
  onSettled?: (result: { txHash: string; pendingDepositId: string }) => void;
  onRejected?: (pendingDepositId: string) => void;
  /** Legacy: parent builds a complete signed XDR (no amount override supported) */
  onBuildSignedXdr?: (deposit: PendingDeposit) => Promise<string>;
  /** Legacy: parent builds WebAuthn fields */
  onBuildWebAuthn?: (deposit: PendingDeposit) => Promise<Omit<SettleParams, 'pendingDepositId'>>;
  /**
   * Preferred signing callback — parent receives the deposit + user-chosen
   * amount (in stroops), prepares an unsigned XDR, signs it with whatever
   * method is connected (Freighter or passkeys), and returns the signed XDR.
   * When provided, the panel never shows manual sign-in fallback forms.
   */
  onSignXdr?: (deposit: PendingDeposit, amountStroops: number) => Promise<string | { signedXdr: string; useServiceAccount: boolean }>;
  /** When both wallet and passkey are available, parent passes both signers so user can choose */
  onSignXdrWallet?:  (deposit: PendingDeposit, amountStroops: number) => Promise<{ signedXdr: string; useServiceAccount: boolean }>;
  onSignXdrPasskey?: (deposit: PendingDeposit, amountStroops: number) => Promise<{ signedXdr: string; useServiceAccount: boolean }>;
  /** Whether both signing methods are available (to show the toggle) */
  hasBothSigners?: boolean;
  network?: 'testnet' | 'mainnet';
  pollIntervalMs?: number;
  /** Fires whenever the pending count changes — used for badge in hamburger menu */
  onPendingCountChange?: (count: number) => void;
  allowedCountries?: Set<number>;
}

export const GeoLinkPendingDepositsPanel: React.FC<GeoLinkPendingDepositsPanelProps> = ({
  walletPublicKey,
  passkeyAccountAddress,
  onSettled,
  onRejected,
  onBuildSignedXdr,
  onBuildWebAuthn,
  onSignXdr,
  onSignXdrWallet,
  onSignXdrPasskey,
  hasBothSigners = false,
  network = 'testnet',
  pollIntervalMs = 15_000,
  onPendingCountChange,
  allowedCountries,
}) => {
  // Preferred signing method when both are available
  const [preferredSigner, setPreferredSigner] = useState<'wallet' | 'passkey'>(
    walletPublicKey?.startsWith('C') ? 'passkey' : 'wallet'
  );
  // Available XLM balance for the active signer
  const [signerBalance, setSignerBalance] = useState<number | null>(null);
  const [deposits, setDeposits]         = useState<PendingDeposit[]>([]);
  const [loading, setLoading]           = useState(false);
  const [syncing, setSyncing]           = useState(false);
  const [lastSyncCount, setLastSyncCount] = useState<number | null>(null);
  const [error, setError]               = useState<string | null>(null);
  const [settling, setSettling]         = useState<string | null>(null);
  const [rejecting, setRejecting]       = useState<string | null>(null);
  const [expandedId, setExpandedId]     = useState<string | null>(null);
  const [historyPage, setHistoryPage]   = useState(0);
  const [historyOpen, setHistoryOpen]   = useState(false);
  const HISTORY_PAGE_SIZE = 5;
  const [ruleDetailInfo, setRuleDetailInfo] = useState<RuleMatchInfo | null>(null);

  // ── Amount editing, country detection, policy ────────────────────────────
  const [depositAmounts, setDepositAmounts]   = useState<Record<string, string>>({});
  // Signing fallback inputs keyed by pendingDepositId
  const [secretKeyInputs, setSecretKeyInputs] = useState<Record<string, string>>({});
  const [signedXdrInputs, setSignedXdrInputs] = useState<Record<string, string>>({});
  // Which signing tab is open per deposit: 'xdr' | 'key'
  const [signingTab, setSigningTab]           = useState<Record<string, 'xdr' | 'key'>>({});
  // Detected country from browser GPS
  const [detectedCountry, setDetectedCountry] = useState<{ code: number; alpha2: string } | null>(null);
  const [countryPolicy, setCountryPolicy]     = useState<CountryPolicyV2 | null>(null);
  const [freighterAvailable, setFreighterAvailable] = useState(false);

  // Detect user's country from GPS on mount, then load compliance policy
  useEffect(() => {
    // Check if a browser wallet extension is installed (Freighter, Rabet, xBull, etc.)
    if (window.freighter) {
      window.freighter.isConnected()
        .then(ok => setFreighterAvailable(ok))
        .catch(() => setFreighterAvailable(false));
    }

    if (!navigator.geolocation) return;
    const mapboxToken = process.env.REACT_APP_MAPBOX_TOKEN;
    if (!mapboxToken) return;

    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          const { latitude, longitude } = pos.coords;
          const geoRes = await fetch(
            `https://api.mapbox.com/geocoding/v5/mapbox.places/${longitude},${latitude}.json` +
            `?access_token=${mapboxToken}&types=country`,
          );
          const geoData = await geoRes.json();
          const shortCode: string | undefined =
            geoData.features?.[0]?.properties?.short_code?.toUpperCase();
          if (!shortCode) return;

          // Fetch country policy from GeoTrust compliance contract
          const polRes = await fetch(`/api/v2/compliance/policy/${shortCode}`);
          if (!polRes.ok) return;
          const polData = await polRes.json();
          if (polData.numericCode) {
            setDetectedCountry({ code: polData.numericCode, alpha2: shortCode });
          }
          if (polData.policy) {
            setCountryPolicy(polData.policy);
          }
        } catch (_) {
          // Non-fatal — UI degrades gracefully
        }
      },
      () => { /* Geolocation denied — country detection unavailable */ },
      { timeout: 8000 },
    );
  }, []);

  // Fetch XLM balance for the active signer whenever it changes
  useEffect(() => {
    const isPasskeyWallet = walletPublicKey?.startsWith('C');
    const addr = preferredSigner === 'passkey'
      ? (passkeyAccountAddress || (isPasskeyWallet ? walletPublicKey : null))
      : walletPublicKey;
    if (!addr) { setSignerBalance(null); return; }
    const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
    fetch(`${backendUrl}/api/passkey-kit/account-balance`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ accountAddress: addr }),
    })
      .then(r => r.json())
      .then(d => { if (d.success) setSignerBalance(d.balance / 10_000_000); })
      .catch(() => {});
  }, [preferredSigner, walletPublicKey, passkeyAccountAddress]);

  // Get the user-edited XLM amount for a deposit, or fall back to the rule's amount
  const getAmountXlm = useCallback((deposit: PendingDeposit): string => {
    if (depositAmounts[deposit.pendingDepositId] !== undefined) {
      return depositAmounts[deposit.pendingDepositId];
    }
    const stroops = parseInt(deposit.amount || '0');
    if (!stroops || isNaN(stroops)) return '';
    return (stroops / 10_000_000).toFixed(7);
  }, [depositAmounts]);

  // Compute fee breakdown from country policy
  const calcFee = useCallback((amountXlm: string) => {
    if (!countryPolicy) return null;
    const amount = parseFloat(amountXlm || '0');
    if (!amount || amount <= 0) return null;
    const feeBps = parseInt(countryPolicy.feeBps || '0');
    if (!feeBps) return null;
    const feeXlm = amount * feeBps / 10000;
    const netXlm = amount - feeXlm;
    const adminShareBps = parseInt(countryPolicy.countryAdminShareBps || '0');
    return {
      feeBps,
      pct:        (feeBps / 100).toFixed(2),
      feeXlm:     feeXlm.toFixed(7),
      netXlm:     netXlm.toFixed(7),
      adminShareBps,
      adminPct:   (adminShareBps / 100).toFixed(2),
      protocolPct:((10000 - adminShareBps) / 100).toFixed(2),
    };
  }, [countryPolicy]);

  // Validate amount against country policy; returns error string or null
  const validateAmount = useCallback((amountXlm: string): string | null => {
    if (!countryPolicy || !detectedCountry) return null;
    if (!countryPolicy.depositsEnabled) return 'Deposits are disabled for your country';
    const amount = parseFloat(amountXlm || '0');
    if (!amount || amount <= 0) return 'Enter a deposit amount greater than 0';
    const amountStroops = Math.round(amount * 10_000_000);
    const min = parseInt(countryPolicy.minTxAmount || '0');
    const max = parseInt(countryPolicy.maxTxAmount || '0');
    if (min && amountStroops < min) {
      return `Minimum deposit is ${(min / 10_000_000).toFixed(7)} XLM`;
    }
    if (max && amountStroops > max) {
      return `Maximum deposit is ${(max / 10_000_000).toFixed(7)} XLM`;
    }
    return null;
  }, [countryPolicy, detectedCountry]);

  const load = useCallback(async (withSync = false) => {
    if (!walletPublicKey) return;

    if (withSync) {
      setSyncing(true);
      try {
        const syncResult = await syncFromGeoLink(walletPublicKey);
        if (syncResult.newDepositsCount > 0) {
          setLastSyncCount(syncResult.newDepositsCount);
        } else {
          setLastSyncCount(prev => (prev ?? 0) > 0 ? prev : null);
        }
      } catch (_) {
        // non-fatal
      } finally {
        setSyncing(false);
      }
    }

    setLoading(true);
    setError(null);
    try {
      const items = await listPendingDeposits({ walletPublicKey });
      const sorted = items.sort((a, b) => {
        const order = { pending: 0, approved: 1, settled: 2, rejected: 3 };
        return (order[a.geoTrustDecision] - order[b.geoTrustDecision])
          || new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
      });
      setDeposits(sorted);
      onPendingCountChange?.(sorted.filter(d => d.geoTrustDecision === 'pending').length);
    } catch (err: any) {
      setError(err.message || 'Failed to load pending deposits');
    } finally {
      setLoading(false);
    }
  }, [walletPublicKey, onPendingCountChange]);

  useEffect(() => {
    load(true);
    if (pollIntervalMs > 0) {
      const id = setInterval(() => load(true), pollIntervalMs);
      return () => clearInterval(id);
    }
  }, [load, pollIntervalMs]);

  const handleSettle = async (deposit: PendingDeposit) => {
    setSettling(deposit.pendingDepositId);
    setError(null);
    try {
      const amountXlm   = getAmountXlm(deposit);
      const amountStroops = Math.round(parseFloat(amountXlm || '0') * 10_000_000);

      // Validate against country policy (if we have one)
      const validErr = validateAmount(amountXlm);
      if (validErr) { setError(validErr); setSettling(null); return; }

      // Client-side balance check — prevents wasting a simulation call
      if (signerBalance !== null && parseFloat(amountXlm) > signerBalance) {
        const signerLabel = preferredSigner === 'passkey' ? 'passkey account' : 'wallet';
        setError(
          `Insufficient balance: your ${signerLabel} holds ${signerBalance.toFixed(4)} XLM ` +
          `but you tried to deposit ${parseFloat(amountXlm).toFixed(4)} XLM.`
        );
        setSettling(null); return;
      }

      let authFields: Omit<SettleParams, 'pendingDepositId'> = {};

      // ── Priority 1: WebAuthn callback from parent ─────────────────────────
      if (onBuildWebAuthn) {
        authFields = await onBuildWebAuthn(deposit);

      // ── Priority 2a: per-signer callbacks when user has toggled a preference
      } else if (hasBothSigners && amountStroops > 0 && (onSignXdrWallet || onSignXdrPasskey)) {
        const signerFn = preferredSigner === 'passkey' ? onSignXdrPasskey : onSignXdrWallet;
        if (!signerFn) {
          setError(`${preferredSigner === 'passkey' ? 'Passkey' : 'Wallet'} signer not available.`);
          setSettling(null); return;
        }
        try {
          const result = await signerFn(deposit, amountStroops);
          authFields = { signedXdr: result.signedXdr, useServiceAccount: result.useServiceAccount };
        } catch (err: any) {
          setError(err.message || 'Signing cancelled or failed');
          setSettling(null); return;
        }

      // ── Priority 2b: onSignXdr — unified wallet callback (Freighter / passkey)
      } else if (onSignXdr && amountStroops > 0) {
        try {
          const result = await onSignXdr(deposit, amountStroops);
          if (typeof result === 'string') {
            authFields = { signedXdr: result };
          } else {
            // Passkey path: { signedXdr, useServiceAccount: true }
            authFields = { signedXdr: result.signedXdr, useServiceAccount: result.useServiceAccount };
          }
        } catch (walletErr: any) {
          // Wallet prompt cancelled or failed — surface the error and stop
          setError(walletErr.message || 'Wallet signing was cancelled or failed');
          setSettling(null);
          return;
        }

      // ── Priority 3: Legacy onBuildSignedXdr callback ───────────────────────
      } else if (onBuildSignedXdr) {
        authFields = { signedXdr: await onBuildSignedXdr(deposit) };

      // ── Priority 4: Direct Freighter (window.freighter) ───────────────────
      // Kept as a fallback when SmartContractsOverlay doesn't pass onSignXdr
      } else if (freighterAvailable && walletPublicKey && amountStroops > 0) {
        try {
          const prepResult = await prepareDepositXdr(deposit.pendingDepositId, amountStroops, walletPublicKey);
          if (prepResult.success && prepResult.unsignedXdr) {
            const networkPassphrase = (deposit.network === 'mainnet')
              ? 'Public Global Stellar Network ; September 2015'
              : 'Test SDF Network ; September 2015';
            const sigResult = await window.freighter!.signTransaction(prepResult.unsignedXdr, { networkPassphrase });
            authFields = { signedXdr: typeof sigResult === 'string' ? sigResult : sigResult.signedTransaction };
          }
        } catch (walletErr: any) {
        }
      }

      // ── Priority 5: Paste-in signed XDR ───────────────────────────────────
      if (!authFields.signedXdr && signedXdrInputs[deposit.pendingDepositId]?.trim()) {
        authFields = { signedXdr: signedXdrInputs[deposit.pendingDepositId].trim() };
      }

      // ── Priority 6: Test-mode secret key ─────────────────────────────────
      const testSecretKey = secretKeyInputs[deposit.pendingDepositId]?.trim();
      if (!authFields.signedXdr && testSecretKey) {
        (authFields as any).userSecretKey = testSecretKey;
      }

      // If still no signing method, block and explain clearly
      if (!authFields.signedXdr && !(authFields as any).userSecretKey && !authFields.webauthnSignature) {
        setError(
          'A wallet signature is required. Connect a wallet (Freighter) or set up passkeys, ' +
          'or use the fallback options below to paste a signed XDR or enter a test secret key.',
        );
        setSettling(null);
        return;
      }

      const result = await settlePendingDeposit({
        pendingDepositId: deposit.pendingDepositId,
        overrideAmount:   amountStroops ? String(amountStroops) : undefined,
        userPublicKey:    walletPublicKey || undefined,
        ...authFields,
      });

      if (!result.success) { setError(result.error || 'Settlement failed'); return; }
      await load();
      onSettled?.({ txHash: result.settlementTxHash!, pendingDepositId: deposit.pendingDepositId });
    } catch (err: any) {
      const raw: string = err.message || 'Unexpected error during settlement';
      const insufficientMatch = raw.match(/balance is not sufficient to spend.*?amount:\s*(\d+)/s);
      if (insufficientMatch) {
        const availXlm = (parseInt(insufficientMatch[1], 10) / 10_000_000).toFixed(4);
        const tried    = parseFloat(getAmountXlm(deposit)).toFixed(4);
        const label    = preferredSigner === 'passkey' ? 'passkey account' : 'wallet';
        setError(`Insufficient balance: your ${label} holds ${availXlm} XLM but you tried to deposit ${tried} XLM.`);
      } else if (raw.includes('Simulation failed')) {
        setError('Simulation failed: ' + raw.split('\n')[0].replace('Simulation failed: ', ''));
      } else {
        setError(raw);
      }
    } finally {
      setSettling(null);
    }
  };

  const handleReject = async (deposit: PendingDeposit) => {
    setRejecting(deposit.pendingDepositId);
    setError(null);
    try {
      const result = await rejectPendingDeposit(deposit.pendingDepositId, 'User rejected from UI');
      if (result.success) { await load(); onRejected?.(deposit.pendingDepositId); }
      else setError(result.error || 'Rejection failed');
    } catch (err: any) {
      setError(err.message || 'Unexpected error');
    } finally {
      setRejecting(null);
    }
  };

  const actionable = deposits.filter(d => d.geoTrustDecision === 'pending' || d.geoTrustDecision === 'approved');
  const historical  = deposits.filter(d => d.geoTrustDecision === 'rejected' || d.geoTrustDecision === 'settled');
  const historySlice = historical.slice(historyPage * HISTORY_PAGE_SIZE, (historyPage + 1) * HISTORY_PAGE_SIZE);
  const totalHistPages = Math.ceil(historical.length / HISTORY_PAGE_SIZE);

  if (!walletPublicKey) {
    return (
      <div style={{ padding: '32px', textAlign: 'center', color: C.textFaint, fontSize: '13px' }}>
        Connect a wallet to view GeoLink-qualified deposits.
      </div>
    );
  }

  return (
    <>
    <div style={{ background: C.bg, minHeight: '100%', padding: '0', fontFamily: 'inherit', color: C.text }}>
      <style>{`
        .gdp-card { transition: background 0.15s; }
        .gdp-card:hover { background: ${C.bgCardHover} !important; }
        .gdp-btn-settle { background: ${C.gold}; color: #000; border: none; border-radius: 6px; font-size: 12px; font-weight: 700; cursor: pointer; transition: opacity 0.15s; }
        .gdp-btn-settle:hover:not(:disabled) { opacity: 0.85; }
        .gdp-btn-settle:disabled { opacity: 0.4; cursor: not-allowed; }
        .gdp-btn-reject { background: transparent; color: ${C.red}; border: 1px solid ${C.red}55; border-radius: 6px; font-size: 12px; font-weight: 600; cursor: pointer; transition: background 0.15s; }
        .gdp-btn-reject:hover:not(:disabled) { background: ${C.redDim}; }
        .gdp-btn-reject:disabled { opacity: 0.4; cursor: not-allowed; }
        .gdp-refresh-btn { background: transparent; border: 1px solid ${C.border}; color: ${C.goldDark}; border-radius: 6px; width: 28px; height: 28px; display: flex; align-items: center; justify-content: center; cursor: pointer; font-size: 14px; transition: background 0.15s; }
        .gdp-refresh-btn:hover:not(:disabled) { background: ${C.goldDim}; }
        .gdp-refresh-btn:disabled { opacity: 0.4; cursor: not-allowed; }
      `}</style>

      {/* ── Header ─────────────────────────────────────────────────────── */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: '10px',
        padding: '14px 16px 10px',
        borderBottom: `1px solid ${C.borderFaint}`,
      }}>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontSize: '13px', fontWeight: 700, color: C.text }}>
              GeoLink-Qualified Deposits
            </span>
            {actionable.length > 0 && (
              <span style={{
                background: C.amberDim, color: C.amber, border: `1px solid ${C.amber}44`,
                borderRadius: '10px', padding: '1px 7px', fontSize: '10px', fontWeight: 700,
              }}>
                {actionable.length} pending
              </span>
            )}
          </div>
          <div style={{ fontSize: '11px', color: C.textFaint, marginTop: '2px' }}>
            {syncing
              ? '⟳ Syncing from GeoLink…'
              : lastSyncCount != null && lastSyncCount > 0
                ? <span style={{ color: C.goldDark, fontWeight: 600 }}>+{lastSyncCount} new from GeoLink</span>
                : 'Qualified by GeoLink · Awaiting GeoTrust settlement'}
          </div>
        </div>
        <button
          className="gdp-refresh-btn"
          onClick={() => load(true)}
          disabled={loading || syncing}
          title="Sync from GeoLink and refresh"
        >
          {loading || syncing ? <span style={{ fontSize: '10px' }}>…</span> : '↻'}
        </button>
      </div>

      {/* ── Error ──────────────────────────────────────────────────────── */}
      {error && (
        <div style={{
          margin: '10px 14px', padding: '9px 12px', borderRadius: '7px',
          background: C.redDim, border: `1px solid ${C.red}44`,
          color: C.red, fontSize: '12px',
        }}>
          ⚠ {error}
        </div>
      )}

      {/* ── Info banner ─────────────────────────────────────────────── */}
      {actionable.length > 0 && (
        <div style={{
          margin: '10px 14px 0', padding: '9px 12px', borderRadius: '7px',
          background: C.goldDim, border: `1px solid ${C.gold}88`,
          color: C.goldDark, fontSize: '11px', lineHeight: '1.6',
        }}>
          <strong>📍 These rules matched your location.</strong>{' '}
          Expand a deposit to set your amount, review your country&apos;s fees, and click <strong>Execute Deposit</strong>.
          {detectedCountry && countryPolicy && (
            <span> Your country: <strong>{detectedCountry.alpha2}</strong>
              {parseInt(countryPolicy.feeBps || '0') > 0 && (
                <span> · Fee: {(parseInt(countryPolicy.feeBps) / 100).toFixed(2)}%</span>
              )}
            </span>
          )}
        </div>
      )}

      {/* ── Actionable deposits ─────────────────────────────────────────── */}
      <div style={{ padding: '10px 14px 0' }}>
        {actionable.length === 0 && !loading && (
          <div style={{
            textAlign: 'center', padding: '28px 0',
            color: C.textFaint, fontSize: '12px',
          }}>
            No pending deposits.
          </div>
        )}
        {loading && actionable.length === 0 && (
          <div style={{ textAlign: 'center', padding: '20px 0', color: C.textFaint, fontSize: '12px' }}>
            Loading…
          </div>
        )}
        {actionable.map(deposit => (
          <DepositCard
            key={deposit.pendingDepositId}
            deposit={deposit}
            network={network}
            isSettling={settling === deposit.pendingDepositId}
            isRejecting={rejecting === deposit.pendingDepositId}
            canAct={!settling && !rejecting}
            expanded={expandedId === deposit.pendingDepositId}
            onToggleExpand={() => setExpandedId(prev =>
              prev === deposit.pendingDepositId ? null : deposit.pendingDepositId)}
            onSettle={handleSettle}
            onReject={handleReject}
            onViewRuleDetail={() => setRuleDetailInfo({
              ruleId:          deposit.ruleId,
              ruleName:        deposit.ruleName,
              contractAddress: deposit.sourceContractAddress,
              functionName:    deposit.sourceContractFunction,
              network:         deposit.network,
              ruleLocation:    deposit.location ?? null,
              matchedAt:       deposit.geoLinkQualifiedAt,
              status:          deposit.geoTrustDecision === 'settled' ? 'completed' : deposit.geoTrustDecision === 'rejected' ? 'rejected' : 'pending',
              transactionHash: deposit.settlementTxHash ?? null,
            })}
            amountXlm={getAmountXlm(deposit)}
            onAmountChange={(id, xlm) => setDepositAmounts(prev => ({ ...prev, [id]: xlm }))}
            detectedCountry={detectedCountry}
            countryPolicy={countryPolicy}
            calcFee={calcFee}
            validateAmount={validateAmount}
            freighterAvailable={freighterAvailable}
            walletSignerReady={!!(onSignXdr || hasBothSigners)}
            hasBothSigners={hasBothSigners}
            preferredSigner={preferredSigner}
            onPreferredSignerChange={setPreferredSigner}
            signerBalance={signerBalance}
            secretKeyInput={secretKeyInputs[deposit.pendingDepositId] ?? ''}
            signedXdrInput={signedXdrInputs[deposit.pendingDepositId] ?? ''}
            signingTabId={signingTab[deposit.pendingDepositId] ?? 'xdr'}
            onSecretKeyChange={(id, val) => setSecretKeyInputs(prev => ({ ...prev, [id]: val }))}
            onSignedXdrChange={(id, val) => setSignedXdrInputs(prev => ({ ...prev, [id]: val }))}
            onSigningTabChange={(id, tab) => setSigningTab(prev => ({ ...prev, [id]: tab }))}
          />
        ))}
      </div>

      {/* ── History section ─────────────────────────────────────────────── */}
      {historical.length > 0 && (
        <div style={{ padding: '12px 14px 14px' }}>
          <button
            onClick={() => setHistoryOpen(o => !o)}
            style={{
              display: 'flex', alignItems: 'center', gap: '7px', width: '100%',
              background: 'none', border: 'none', cursor: 'pointer',
              padding: '8px 10px', borderRadius: '7px',
              color: C.textMuted, fontSize: '12px', fontWeight: 600,
              borderTop: `1px solid ${C.borderFaint}`, paddingTop: '12px',
            }}
          >
            <span style={{ transform: historyOpen ? 'rotate(90deg)' : 'none', transition: 'transform 0.15s', fontSize: '10px' }}>▶</span>
            History ({historical.length})
            <span style={{
              marginLeft: 'auto', display: 'flex', gap: '5px',
            }}>
              {['settled', 'rejected'].map(d => {
                const count = historical.filter(x => x.geoTrustDecision === d).length;
                if (!count) return null;
                const cfg = DECISION_CONFIG[d as PendingDeposit['geoTrustDecision']];
                return (
                  <span key={d} style={{
                    fontSize: '10px', padding: '1px 6px', borderRadius: '9px',
                    background: cfg.dim, color: cfg.color, border: `1px solid ${cfg.color}44`,
                  }}>
                    {count} {cfg.label}
                  </span>
                );
              })}
            </span>
          </button>

          {historyOpen && (
            <>
              {historySlice.map(deposit => (
                <DepositCard
                  key={deposit.pendingDepositId}
                  deposit={deposit}
                  network={network}
                  isSettling={false}
                  isRejecting={false}
                  canAct={false}
                  expanded={expandedId === deposit.pendingDepositId}
                  onToggleExpand={() => setExpandedId(prev =>
                    prev === deposit.pendingDepositId ? null : deposit.pendingDepositId)}
                  onSettle={() => {}}
                  onReject={() => {}}
                  onViewRuleDetail={() => setRuleDetailInfo({
                    ruleId:          deposit.ruleId,
                    ruleName:        deposit.ruleName,
                    contractAddress: deposit.sourceContractAddress,
                    functionName:    deposit.sourceContractFunction,
                    network:         deposit.network,
                    ruleLocation:    deposit.location ?? null,
                    matchedAt:       deposit.geoLinkQualifiedAt,
                    status:          deposit.geoTrustDecision === 'settled' ? 'completed' : deposit.geoTrustDecision === 'rejected' ? 'rejected' : 'pending',
                    transactionHash: deposit.settlementTxHash ?? null,
                  })}
                  readonly
                  amountXlm={getAmountXlm(deposit)}
                  onAmountChange={() => {}}
                  detectedCountry={detectedCountry}
                  countryPolicy={countryPolicy}
                  calcFee={calcFee}
                  validateAmount={validateAmount}
                  freighterAvailable={false}
                  walletSignerReady={false}
                  hasBothSigners={false}
                  preferredSigner={preferredSigner}
                  onPreferredSignerChange={() => {}}
                  signerBalance={null}
                  secretKeyInput=""
                  signedXdrInput=""
                  signingTabId="xdr"
                  onSecretKeyChange={() => {}}
                  onSignedXdrChange={() => {}}
                  onSigningTabChange={() => {}}
                />
              ))}

              {/* Pagination */}
              {totalHistPages > 1 && (
                <div style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  gap: '6px', marginTop: '10px',
                }}>
                  <button
                    onClick={() => setHistoryPage(p => Math.max(0, p - 1))}
                    disabled={historyPage === 0}
                    style={paginationBtnStyle(historyPage === 0)}
                  >←</button>
                  <span style={{ fontSize: '11px', color: C.textFaint }}>
                    {historyPage + 1} / {totalHistPages}
                  </span>
                  <button
                    onClick={() => setHistoryPage(p => Math.min(totalHistPages - 1, p + 1))}
                    disabled={historyPage >= totalHistPages - 1}
                    style={paginationBtnStyle(historyPage >= totalHistPages - 1)}
                  >→</button>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>

    {/* Rule Match Detail Overlay */}
    {ruleDetailInfo && (
      <RuleMatchDetailOverlay
        isOpen={!!ruleDetailInfo}
        onClose={() => setRuleDetailInfo(null)}
        info={ruleDetailInfo}
        network={network}
        allowedCountries={allowedCountries}
      />
    )}
    </>
  );
};

function paginationBtnStyle(disabled: boolean) {
  return {
    background: disabled ? '#f5f5f5' : C.gold,
    border: `1px solid ${disabled ? '#ddd' : C.gold}`,
    color: disabled ? '#aaa' : '#000',
    borderRadius: '5px', width: '26px', height: '26px',
    cursor: disabled ? 'not-allowed' : 'pointer',
    fontSize: '12px', fontWeight: 700,
  } as React.CSSProperties;
}

// ── Deposit Card ─────────────────────────────────────────────────────────────

interface DepositCardProps {
  deposit:          PendingDeposit;
  network:          'testnet' | 'mainnet';
  isSettling:       boolean;
  isRejecting:      boolean;
  canAct:           boolean;
  expanded:         boolean;
  readonly?:        boolean;
  onToggleExpand:     () => void;
  onSettle:           (d: PendingDeposit) => void;
  onReject:           (d: PendingDeposit) => void;
  onViewRuleDetail?:  () => void;
  // Amount editing + country policy
  amountXlm:        string;
  onAmountChange:   (depositId: string, xlm: string) => void;
  detectedCountry:  { code: number; alpha2: string } | null;
  countryPolicy:    CountryPolicyV2 | null;
  calcFee:          (xlm: string) => { feeBps: number; pct: string; feeXlm: string; netXlm: string; adminShareBps: number; adminPct: string; protocolPct: string } | null;
  validateAmount:   (xlm: string) => string | null;
  freighterAvailable:  boolean;
  walletSignerReady:   boolean;   // true when onSignXdr is available from parent
  hasBothSigners:      boolean;
  preferredSigner:     'wallet' | 'passkey';
  onPreferredSignerChange: (s: 'wallet' | 'passkey') => void;
  signerBalance:       number | null;
  secretKeyInput:      string;
  signedXdrInput:      string;
  signingTabId:        'xdr' | 'key';
  onSecretKeyChange:   (depositId: string, val: string) => void;
  onSignedXdrChange:   (depositId: string, val: string) => void;
  onSigningTabChange:  (depositId: string, tab: 'xdr' | 'key') => void;
}

const DepositCard: React.FC<DepositCardProps> = ({
  deposit, network, isSettling, isRejecting, canAct,
  expanded, readonly = false, onToggleExpand, onSettle, onReject, onViewRuleDetail,
  amountXlm, onAmountChange, detectedCountry, countryPolicy, calcFee, validateAmount,
  freighterAvailable, walletSignerReady, hasBothSigners, preferredSigner, onPreferredSignerChange,
  signerBalance,
  secretKeyInput, signedXdrInput, signingTabId, onSecretKeyChange, onSignedXdrChange, onSigningTabChange,
}) => {
  const cfg           = DECISION_CONFIG[deposit.geoTrustDecision];
  // Use deposit's own country code when present; fall back to GPS-detected country
  const resolvedCountryCode  = deposit.countryCode || detectedCountry?.code || 0;
  const resolvedCountryAlpha = numericToAlpha2(resolvedCountryCode)
    ?? detectedCountry?.alpha2
    ?? (resolvedCountryCode ? String(resolvedCountryCode) : '—');
  const countryAlpha2        = resolvedCountryAlpha;
  const amountDisplay = (() => {
    try { return stroopsToDisplay(deposit.amount, 7); } catch { return deposit.amount || '0.0000000'; }
  })();
  const shortAddr     = (addr: string) => addr ? `${addr.slice(0, 8)}…${addr.slice(-6)}` : '—';
  const shortRuleMatch = deposit.ruleMatchId.length > 12
    ? deposit.ruleMatchId.slice(-8)
    : deposit.ruleMatchId;

  // Live fee/validation from user-entered amount
  const feeInfo      = expanded ? calcFee(amountXlm) : null;
  const amountError  = expanded ? validateAmount(amountXlm) : null;
  const amountStroops = Math.round(parseFloat(amountXlm || '0') * 10_000_000);

  const kindLabel = () => {
    const k = deposit.protocolClassification?.kind;
    if (!k || k === 'unknown') return null;
    const map: Record<string, string> = {
      factory: 'DeFindex Factory', vault: 'DeFindex Vault',
      strategy: 'DeFindex Strategy', 'compatible-strategy': 'Compatible Strategy',
    };
    return map[k] ?? k;
  };

  return (
    <div
      className="gdp-card"
      style={{
        background: C.bgCard,
        border: `2px solid ${cfg.border}`,
        borderRadius: '9px',
        marginBottom: '10px',
        overflow: 'hidden',
        boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
      }}
    >
      {/* ── Summary row ─────────────────────────────────────────────── */}
      <div
        onClick={onToggleExpand}
        style={{ padding: '11px 13px', cursor: 'pointer' }}
      >
        {/* Row 1: status badge + rule name + expand */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
          <span style={{
            display: 'inline-flex', alignItems: 'center', gap: '4px',
            background: cfg.dim, color: cfg.color,
            border: `1px solid ${cfg.color}44`,
            borderRadius: '5px', padding: '2px 8px',
            fontSize: '10px', fontWeight: 700, letterSpacing: '0.3px',
            flexShrink: 0,
          }}>
            {cfg.icon} {cfg.label}
          </span>
          <span style={{ fontSize: '12px', fontWeight: 700, color: '#000', flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {deposit.ruleName || deposit.sourceContractFunction || 'Unknown function'}
          </span>
          <span style={{ fontSize: '11px', color: C.textFaint, flexShrink: 0 }}>
            {expanded ? '▲' : '▼'}
          </span>
        </div>

        {/* Row 2: amount · contract · network · rule match */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center', marginBottom: '5px' }}>
          <span style={{ fontSize: '13px', fontWeight: 700, color: '#000', fontVariantNumeric: 'tabular-nums' }}>
            {amountDisplay}
            {deposit.asset && (
              <span style={{ fontSize: '10px', color: C.textMuted, marginLeft: '4px' }}>
                {deposit.asset.slice(0, 6)}
              </span>
            )}
          </span>
          <span style={{
            background: C.goldDim, color: C.goldDark,
            borderRadius: '4px', padding: '1px 7px', fontSize: '10px',
            border: `1px solid ${C.gold}88`, fontWeight: 600,
          }}>
            {deposit.network}
          </span>
          {kindLabel() && (
            <span style={{
              background: 'rgba(0,123,255,0.08)', color: C.blue,
              borderRadius: '4px', padding: '1px 7px', fontSize: '10px',
              border: '1px solid rgba(0,123,255,0.2)',
            }}>
              {kindLabel()}
            </span>
          )}
          <span style={{ fontSize: '10px', color: C.textFaint, marginLeft: 'auto' }}>
            Rule match: <span style={{ fontFamily: 'monospace' }}>{shortRuleMatch}</span>
          </span>
        </div>

        {/* Row 3: contract · country · timestamp */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', alignItems: 'center' }}>
          <span style={{ fontSize: '11px', color: C.textMuted }}>
            ○ <span style={{ fontFamily: 'monospace', fontSize: '10px' }}>{shortAddr(deposit.sourceContractAddress)}</span>
          </span>
          <span style={{ fontSize: '11px', color: C.textMuted }}>
            Country: <strong style={{ color: C.text }}>{countryAlpha2}</strong>
            {resolvedCountryCode > 0 && (
              <span style={{ color: C.textFaint }}>
                {' '}({resolvedCountryCode}){deposit.countryCode === 0 && <span title="detected from GPS"> · 📍</span>}
              </span>
            )}
          </span>
          <span style={{ fontSize: '10px', color: C.textFaint, marginLeft: 'auto' }}>
            {new Date(deposit.geoLinkQualifiedAt).toLocaleString()}
          </span>
        </div>

        {/* Rejection reason inline */}
        {deposit.decisionReason && (
          <div style={{
            marginTop: '6px', fontSize: '11px', color: C.red,
            background: C.redDim, borderRadius: '5px', padding: '4px 8px',
          }}>
            Reason: {deposit.decisionReason}
          </div>
        )}
      </div>

      {/* ── Expanded detail ────────────────────────────────────────────── */}
      {expanded && (
        <div style={{ borderTop: `1px solid ${C.borderFaint}`, padding: '12px 14px', background: '#fff' }}>

          {/* ── Amount editor ─────────────────────────────────────────── */}
          {!readonly && (deposit.geoTrustDecision === 'pending' || deposit.geoTrustDecision === 'approved') && (
            <div style={{ marginBottom: '14px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '6px' }}>
                <div style={{ fontSize: '11px', fontWeight: 700, color: C.text }}>
                  Deposit Amount
                </div>
                {signerBalance !== null && (
                  <span style={{ fontSize: '10px', color: C.textFaint }}>
                    Available: <strong style={{ color: C.text }}>{signerBalance.toFixed(4)} XLM</strong>
                    <button
                      onClick={e => { e.stopPropagation(); onAmountChange(deposit.pendingDepositId, signerBalance.toFixed(7)); }}
                      style={{ marginLeft: 5, fontSize: 9, padding: '1px 5px', borderRadius: 3,
                        border: `1px solid ${C.border}`, background: C.bgCard,
                        cursor: 'pointer', color: C.goldDark, fontWeight: 700 }}>
                      MAX
                    </button>
                  </span>
                )}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <input
                  type="number"
                  min="0"
                  step="0.0000001"
                  value={amountXlm}
                  onChange={e => { e.stopPropagation(); onAmountChange(deposit.pendingDepositId, e.target.value); }}
                  onClick={e => e.stopPropagation()}
                  placeholder="0.0000000"
                  style={{
                    flex: 1, padding: '8px 10px',
                    border: `1px solid ${(amountError || (signerBalance !== null && parseFloat(amountXlm) > signerBalance)) ? C.red : C.border}`,
                    borderRadius: '6px', fontSize: '14px',
                    fontFamily: 'monospace', color: C.text,
                    background: '#fff', outline: 'none',
                  }}
                />
                <span style={{ fontSize: '13px', fontWeight: 600, color: C.textMuted, minWidth: '28px' }}>XLM</span>
              </div>
              {signerBalance !== null && parseFloat(amountXlm) > signerBalance && !amountError && (
                <div style={{ fontSize: '11px', color: C.red, marginTop: '4px', fontWeight: 600 }}>
                  ⚠ Exceeds available balance ({signerBalance.toFixed(4)} XLM)
                </div>
              )}
              {amountStroops > 0 && (
                <div style={{ fontSize: '10px', color: C.textFaint, marginTop: '3px' }}>
                  = {amountStroops.toLocaleString()} stroops
                  {deposit.amount && parseInt(deposit.amount) !== amountStroops && (
                    <span style={{ marginLeft: '8px', color: C.amber }}>
                      (rule stored: {stroopsToDisplay(deposit.amount, 7)} XLM)
                    </span>
                  )}
                </div>
              )}
              {amountError && (
                <div style={{ fontSize: '11px', color: C.red, marginTop: '4px', fontWeight: 600 }}>
                  ⚠ {amountError}
                </div>
              )}

              {/* Fee breakdown */}
              {feeInfo && !amountError && (
                <div style={{
                  marginTop: '8px', padding: '9px 11px', borderRadius: '7px',
                  background: '#fffbe6', border: `1px solid #ffd70088`,
                  fontSize: '12px', lineHeight: '1.6',
                }}>
                  <div style={{ fontWeight: 700, marginBottom: '4px', color: '#7a5c00' }}>
                    {detectedCountry?.alpha2} Policy — Fee Breakdown
                  </div>
                  <div style={{ color: '#665200' }}>
                    Fee ({feeInfo.pct}%): <strong>{feeInfo.feeXlm} XLM</strong>
                  </div>
                  <div style={{ color: '#665200' }}>
                    You receive: <strong>{feeInfo.netXlm} XLM</strong>
                  </div>
                  <div style={{ fontSize: '10px', color: '#9c7800', marginTop: '3px' }}>
                    {feeInfo.adminPct}% of fee → country admin · {feeInfo.protocolPct}% → protocol
                  </div>
                </div>
              )}

              {/* Country policy info */}
              {detectedCountry && countryPolicy && !feeInfo && !amountError && amountStroops > 0 && (
                <div style={{
                  marginTop: '6px', fontSize: '10px', color: C.textFaint,
                  padding: '5px 8px', borderRadius: '5px', background: '#f5f5f5', border: `1px solid ${C.borderFaint}`,
                }}>
                  📍 {detectedCountry.alpha2} · No fees ·{' '}
                  {parseInt(countryPolicy.minTxAmount || '0') > 0 &&
                    `Min ${(parseInt(countryPolicy.minTxAmount) / 10_000_000).toFixed(2)} XLM · `}
                  {parseInt(countryPolicy.maxTxAmount || '0') > 0 &&
                    `Max ${(parseInt(countryPolicy.maxTxAmount) / 10_000_000).toFixed(2)} XLM`}
                </div>
              )}
              {detectedCountry && !countryPolicy && (
                <div style={{ marginTop: '5px', fontSize: '10px', color: C.textFaint }}>
                  📍 Detected: {detectedCountry.alpha2} · No GeoTrust policy found
                </div>
              )}

              {/* Signing source toggle — shown when both wallet AND passkey are available */}
              {hasBothSigners && (
                <div style={{ marginBottom: 8 }}>
                  <div style={{ fontSize: 10, color: C.textFaint, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 5 }}>
                    Sign with
                  </div>
                  <div style={{ display: 'flex', borderRadius: 8, overflow: 'hidden', border: `1px solid ${C.border}`, width: 'fit-content' }}>
                    {(['wallet', 'passkey'] as const).map(method => (
                      <button
                        key={method}
                        onClick={e => { e.stopPropagation(); onPreferredSignerChange(method); }}
                        style={{
                          padding: '5px 14px', fontSize: 11, fontWeight: preferredSigner === method ? 700 : 500,
                          background: preferredSigner === method ? C.gold : '#fff',
                          color: preferredSigner === method ? '#000' : C.textFaint,
                          border: 'none', cursor: 'pointer',
                          borderRight: method === 'wallet' ? `1px solid ${C.border}` : 'none',
                        }}
                      >
                        {method === 'wallet' ? '💳 Wallet' : '🔑 Passkey'}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Wallet signer availability + fallback signing when no extension */}
              <div style={{ marginTop: '8px' }}>
                {(walletSignerReady || freighterAvailable) ? (
                  <span style={{
                    fontSize: '10px', padding: '2px 7px', borderRadius: '10px',
                    background: '#e6f7ea', color: '#1a7a34', border: '1px solid #a0d9b0',
                  }}>
                    🔑 {walletSignerReady
                      ? (hasBothSigners
                          ? `${preferredSigner === 'passkey' ? 'Passkey' : 'Wallet'} selected — will sign automatically`
                          : 'Wallet connected — will sign automatically')
                      : 'Wallet detected — will sign automatically'}
                  </span>
                ) : (
                  <div style={{ border: `1px solid ${C.border}`, borderRadius: '8px', overflow: 'hidden' }}>
                    {/* Tab selector */}
                    <div style={{ display: 'flex', borderBottom: `1px solid ${C.border}`, background: '#f8f9fa' }}>
                      {(['xdr', 'key'] as const).map(tab => {
                        const active = signingTabId === tab;
                        return (
                          <button
                            key={tab}
                            onClick={e => { e.stopPropagation(); onSigningTabChange(deposit.pendingDepositId, tab); }}
                            style={{
                              flex: 1, padding: '6px', fontSize: '11px', fontWeight: active ? 700 : 500,
                              background: active ? '#fff' : 'transparent',
                              color: active ? C.text : C.textFaint,
                              border: 'none', borderBottom: active ? `2px solid ${C.gold}` : '2px solid transparent',
                              cursor: 'pointer',
                            }}
                          >
                            {tab === 'xdr' ? '📋 Paste Signed XDR' : '🔑 Secret Key (test only)'}
                          </button>
                        );
                      })}
                    </div>

                    {/* XDR tab */}
                    {signingTabId === 'xdr' && (
                      <div style={{ padding: '9px 10px' }}>
                        <div style={{ fontSize: '10px', color: C.textFaint, marginBottom: '4px' }}>
                          Build the transaction externally (e.g. Stellar Lab), sign it with your key, and paste the signed XDR below.
                        </div>
                        <textarea
                          placeholder="Paste signed transaction XDR here…"
                          value={signedXdrInput}
                          onChange={e => { e.stopPropagation(); onSignedXdrChange(deposit.pendingDepositId, e.target.value); }}
                          onClick={e => e.stopPropagation()}
                          rows={3}
                          style={{
                            width: '100%', padding: '6px 8px', fontFamily: 'monospace', fontSize: '10px',
                            border: `1px solid ${C.border}`, borderRadius: '5px', resize: 'vertical',
                            boxSizing: 'border-box', color: C.text, background: '#fff',
                          }}
                        />
                      </div>
                    )}

                    {/* Secret key tab */}
                    {signingTabId === 'key' && (
                      <div style={{ padding: '9px 10px' }}>
                        <div style={{ fontSize: '10px', color: C.amber, marginBottom: '4px', fontWeight: 600 }}>
                          ⚠ Test accounts only — never enter a mainnet secret key here
                        </div>
                        <input
                          type="password"
                          placeholder="S… (test account secret key)"
                          value={secretKeyInput}
                          onChange={e => { e.stopPropagation(); onSecretKeyChange(deposit.pendingDepositId, e.target.value); }}
                          onClick={e => e.stopPropagation()}
                          style={{
                            width: '100%', padding: '6px 8px', fontFamily: 'monospace', fontSize: '11px',
                            border: `1px solid ${C.amber}55`, borderRadius: '5px',
                            boxSizing: 'border-box', color: C.text, background: '#fffbe6',
                          }}
                        />
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ── Rule / contract metadata ─────────────────────────────── */}
          {onViewRuleDetail && (
            <button
              onClick={e => { e.stopPropagation(); onViewRuleDetail(); }}
              style={{
                display: 'flex', alignItems: 'center', gap: '5px',
                marginBottom: '10px', padding: '6px 12px', borderRadius: '7px',
                border: `1px solid #1a1f2e44`, background: '#1a1f2e08',
                color: '#1a1f2e', fontSize: '11px', fontWeight: 600, cursor: 'pointer',
              }}
            >
              🗺 View Rule on Map
            </button>
          )}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px 16px', marginBottom: '10px' }}>
            {([
              ['Rule Name',      deposit.ruleName || '—'],
              ['Rule ID',        deposit.ruleId || '—'],
              ['GeoLink Action', deposit.geolinkActionId || '—'],
              ['Contract',       deposit.sourceContractAddress],
              ['Function',       deposit.sourceContractFunction],
              readonly ? ['Amount', `${amountDisplay} XLM`] : null,
              ['Country',        resolvedCountryCode
                  ? `${countryAlpha2} (${resolvedCountryCode})${deposit.countryCode === 0 ? ' · detected' : ''}`
                  : '—'],
              ['Network',        deposit.network],
              // Only show Protocol/Kind when they carry useful information
              deposit.protocolClassification?.protocol && deposit.protocolClassification.protocol !== 'unknown'
                ? ['Protocol', deposit.protocolClassification.protocol] : null,
              deposit.protocolClassification?.kind && deposit.protocolClassification.kind !== 'unknown'
                ? ['Kind', deposit.protocolClassification.kind] : null,
              deposit.location ? ['Location', `${deposit.location.latitude?.toFixed(4)}, ${deposit.location.longitude?.toFixed(4)}`] : null,
              deposit.expiresAt ? ['Expires',  new Date(deposit.expiresAt).toLocaleString()] : null,
              ['Qualified at',   new Date(deposit.geoLinkQualifiedAt).toLocaleString()],
              ...(deposit.settlementTxHash ? [['Tx Hash', deposit.settlementTxHash]] : []),
            ] as any[]).filter(Boolean).map(([label, value]: [string, any]) => (
              <div key={label}>
                <div style={{ fontSize: '9px', color: C.textFaint, textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: '2px' }}>{label}</div>
                <div style={{ fontSize: '11px', color: '#333', fontFamily: 'monospace', wordBreak: 'break-all' }}>{String(value)}</div>
              </div>
            ))}
          </div>

          {deposit.settlementTxHash && (
            <a
              href={`https://stellar.expert/explorer/${network}/tx/${deposit.settlementTxHash}`}
              target="_blank"
              rel="noopener noreferrer"
              style={{ display: 'inline-block', marginBottom: '10px', fontSize: '11px', color: C.goldDark, textDecoration: 'none' }}
            >
              🔗 View on Stellar Expert ↗
            </a>
          )}

          <div style={{ fontSize: '10px', color: C.textFaint, marginBottom: '10px', padding: '6px 9px', borderRadius: '5px', background: '#f5f5f5', border: `1px solid ${C.borderFaint}` }}>
            {deposit.geolinkStatus === 'completed'
              ? 'GeoLink-qualified · Settled by GeoTrust'
              : deposit.geolinkStatus === 'cancelled'
                ? 'GeoLink-qualified · Cancelled'
                : deposit.geolinkStatus === 'failed'
                  ? 'GeoLink-qualified · Settlement failed'
                  : 'GeoLink-qualified · Awaiting GeoTrust settlement'}
          </div>

          {/* Action buttons */}
          {!readonly && (deposit.geoTrustDecision === 'pending' || deposit.geoTrustDecision === 'approved') && (
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              <button
                className="gdp-btn-settle"
                style={{ padding: '8px 20px', fontSize: '13px', fontWeight: 700, background: amountError ? '#ccc' : C.gold, color: '#000', border: 'none', borderRadius: '6px', cursor: amountError ? 'not-allowed' : 'pointer' }}
                onClick={e => { e.stopPropagation(); if (!amountError) onSettle(deposit); }}
                disabled={!canAct || isSettling || isRejecting || !!amountError}
              >
                {isSettling ? '⏳ Processing…' : '⬆ Execute Deposit'}
              </button>
              <button
                className="gdp-btn-reject"
                style={{ padding: '8px 14px', fontSize: '12px', fontWeight: 600, background: 'transparent', color: C.red, border: `1px solid ${C.red}55`, borderRadius: '6px', cursor: 'pointer' }}
                onClick={e => { e.stopPropagation(); onReject(deposit); }}
                disabled={!canAct || isSettling || isRejecting}
              >
                {isRejecting ? 'Rejecting…' : '✕ Reject'}
              </button>
            </div>
          )}
        </div>
      )}

      {/* ── Bottom notice bar ──────────────────────────────────────────── */}
      {!expanded && (deposit.geoTrustDecision === 'pending' || deposit.geoTrustDecision === 'approved') && (
        <div style={{
          borderTop: `1px solid ${C.borderFaint}`,
          padding: '7px 13px',
          display: 'flex', gap: '8px', alignItems: 'center',
        }}>
          <span style={{ flex: 1, fontSize: '10px', color: C.textFaint }}>
            GeoLink-qualified · expand to set amount &amp; execute
          </span>
          <button
            className="gdp-btn-reject"
            style={{ padding: '4px 10px', fontSize: '11px', fontWeight: 600, background: 'transparent', color: C.red, border: `1px solid ${C.red}44`, borderRadius: '5px', cursor: 'pointer' }}
            onClick={e => { e.stopPropagation(); onReject(deposit); }}
            disabled={!canAct || isSettling || isRejecting}
          >
            {isRejecting ? '…' : '✕ Reject'}
          </button>
        </div>
      )}
    </div>
  );
};

export default GeoLinkPendingDepositsPanel;
